import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { initialise } from '@churnsignal/churnsignal-sdk-web';

import ClerkProviderWithRouter from '@/component-library/widgets/ClerkProviderWithRouter/ClerkProviderWithRouter';
import { isClerkAuthEnabled } from '@/utils/auth/authMode';
import { configureAmplify } from '@/utils/auth/configureAmplify';
import { logRuntimeAuthConfig } from '@/utils/auth/logRuntimeAuthConfig';

import './i18n';

if (!isClerkAuthEnabled()) {
  configureAmplify();
}
logRuntimeAuthConfig();

import App from './app/App';
import store from './app/store';
import { ProvideAuth } from './modules/Auth/hooks/useAuth';
import reportWebVitals from './reportWebVitals';

import './index.css';

// Initialize the SDK
try {
  // The API key format is: cs_{environment}_tenantId_workspaceId_secret
  // The SDK automatically extracts workspaceId, tenantId, and baseUrl from the API key
  const apiKey = process.env.REACT_APP_CHURNSIGNAL_API_KEY || process.env.REACT_APP_CHURNSIGNAL_SDK_API_KEY;

  if (apiKey) {
    const baseUrl = process.env.REACT_APP_CHURNSIGNAL_API_BASE_URL?.trim();
    try {
      initialise(apiKey, baseUrl ? { baseUrl } : undefined);
      console.log('✅ ChurnSignal SDK initialized successfully');
    } catch (initError) {
      console.error('❌ SDK initialization error:', initError);
    }
  } else {
    console.warn('⚠️ ChurnSignal SDK not initialized: Missing required environment variable');
    console.warn('Required: REACT_APP_CHURNSIGNAL_API_KEY or REACT_APP_CHURNSIGNAL_SDK_API_KEY');
    console.warn('API key format: cs_{environment}_tenantId_workspaceId_secret');
  }
} catch (error) {
  console.error('❌ Failed to initialize ChurnSignal SDK:', error);
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 1,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

const clerkPublishableKey = (process.env.REACT_APP_CLERK_PUBLISHABLE_KEY || '').trim();

const appTree = (
  <QueryClientProvider client={queryClient}>
    <Provider store={store}>
      <ProvideAuth>
        <App />
      </ProvideAuth>
    </Provider>
  </QueryClientProvider>
);

root.render(
  // <React.StrictMode>
  <BrowserRouter>
    {clerkPublishableKey ? (
      <ClerkProviderWithRouter publishableKey={clerkPublishableKey}>{appTree}</ClerkProviderWithRouter>
    ) : (
      appTree
    )}
  </BrowserRouter>,
  // </React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
